/**
 * TESTFLOW Logger - Debug logging system for meal plan and grocery list generation
 * 
 * Usage:
 * 1. In browser console, filter by "[TESTFLOW]" to see all debug logs
 * 2. Copy the JSON object for further analysis or bug reporting
 * 3. Use the logged data to diagnose calculation/data issues
 * 
 * The logger captures:
 * - Prompt sent to AI
 * - Recipes returned by AI (mealPlan)
 * - Grocery list (products with matched Typesense data)
 * - Leftovers (calculated from package sizes vs needed amounts)
 * - Context (country, numberOfPeople, budget, etc.)
 */

// Store the last generated prompt for later access
let lastGeneratedPrompt = null;

/**
 * Store the generated prompt for later logging
 * Call this from promptGenerators.js when prompt is generated
 */
export const storePromptForTestFlow = (prompt) => {
  lastGeneratedPrompt = prompt;
};

/**
 * Get the stored prompt
 */
export const getStoredPrompt = () => lastGeneratedPrompt;

/**
 * Clear the stored prompt (call when starting new generation)
 */
export const clearStoredPrompt = () => {
  lastGeneratedPrompt = null;
};

/**
 * Log the complete TESTFLOW data for debugging
 * 
 * @param {Object} params
 * @param {string} params.prompt - The AI prompt that was sent
 * @param {Array} params.recipes - The meal plan recipes from AI
 * @param {Array} params.groceryList - The processed grocery list (products)
 * @param {Array} params.leftovers - The calculated leftovers
 * @param {Object} params.context - Additional context (country, numberOfPeople, etc.)
 */
export const logTestFlow = ({
  prompt = null,
  recipes = [],
  groceryList = [],
  leftovers = [],
  context = {}
}) => {
  const timestamp = new Date().toISOString();
  
  // Use stored prompt if not provided
  const effectivePrompt = prompt || lastGeneratedPrompt;
  
  // Create summary for quick overview
  const summary = {
    timestamp,
    recipesCount: recipes.length,
    recipeTitles: recipes.map(r => r.title || r.name || 'Unknown'),
    groceryItemsCount: countGroceryItems(groceryList),
    leftoversCount: leftovers.length,
    country: context.country || 'Unknown',
    numberOfPeople: context.numberOfPeople || 'Unknown',
  };
  
  // Create detailed data object
  const testFlowData = {
    _TESTFLOW_VERSION: '1.0',
    _SUMMARY: summary,
    
    // Full context
    context: {
      ...context,
      timestamp,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
    },
    
    // The prompt sent to AI
    prompt: effectivePrompt,
    
    // Recipes as returned by AI
    recipes: recipes.map(recipe => ({
      title: recipe.title,
      mealType: recipe.mealType,
      estimatedCost: recipe.estimatedCost,
      ingredients: recipe.ingredients,
      // Include other useful fields but exclude large ones like fullInstructions
      prepTime: recipe.prepTime,
      cookTime: recipe.cookTime,
      totalTime: recipe.totalTime,
      servings: recipe.servings,
    })),
    
    // Grocery list with all product data
    groceryList: groceryList.map(category => ({
      category: category.category,
      items: category.items?.map(item => ({
        // Recipe request
        productName: item.productName,
        requestedQty: item.requestedQty,
        requestedUnit: item.requestedUnit,
        
        // Matched product from Typesense
        typesenseId: item.typesenseId || item.id,
        typesenseName: item.typesenseProductName || item.matchedProductName,
        type: item.type, // 'unit', 'package', 'weight'
        packageSize: item.packageSize,
        packageType: item.packageType,
        measurementUnit: item.measurementUnit,
        packageDescription: item.packageDescription,
        
        // Calculation results
        packagesNeeded: item.packagesNeeded,
        totalQty: item.totalQty,
        price: item.price,
        totalPrice: item.totalPrice,
        
        // Store info
        storeName: item.storeName,
        storeUrl: item.storeUrl,
        productUrl: item.productUrl,
        
        // Match info
        matchScore: item.matchScore,
        matchMethod: item.matchMethod,
      })) || [],
    })),
    
    // Leftovers with calculation details
    leftovers: leftovers.map(leftover => ({
      // Product identification
      productName: leftover.productName,
      productId: leftover.productId || leftover.typesenseId,
      category: leftover.category,
      
      // What was bought
      purchasedAmount: leftover.purchasedAmount,
      purchasedUnit: leftover.purchasedUnit,
      packagesNeeded: leftover.packagesNeeded,
      packageSize: leftover.packageSize,
      packageType: leftover.packageType,
      
      // What was used
      usedAmount: leftover.usedAmount,
      usedUnit: leftover.usedUnit,
      
      // Leftover calculation
      leftoverAmount: leftover.leftoverAmount,
      leftoverUnit: leftover.leftoverUnit,
      displayAmount: leftover.displayAmount,
      displayUnit: leftover.displayUnit,
      
      // For debugging
      type: leftover.type, // 'unit', 'package', 'weight'
      measurementUnit: leftover.measurementUnit,
    })),
  };
  
  // Log to console with TESTFLOW tag
  console.log('\n\n');
  console.log('='.repeat(80));
  console.log('[TESTFLOW] 🧪 MEAL PLAN DEBUG DATA');
  console.log('='.repeat(80));
  console.log('[TESTFLOW] Summary:', JSON.stringify(summary, null, 2));
  console.log('[TESTFLOW] Full data (copy this for bug reports):');
  console.log('[TESTFLOW_DATA]', JSON.stringify(testFlowData, null, 2));
  console.log('='.repeat(80));
  console.log('\n\n');
  
  // Also store in window for easy access in browser console
  if (typeof window !== 'undefined') {
    window.__TESTFLOW_LAST_DATA = testFlowData;
    console.log('[TESTFLOW] 💡 Tip: Access full data via window.__TESTFLOW_LAST_DATA');
  }
  
  return testFlowData;
};

/**
 * Helper to count total grocery items across all categories
 */
const countGroceryItems = (groceryList) => {
  if (!Array.isArray(groceryList)) return 0;
  return groceryList.reduce((sum, category) => 
    sum + (Array.isArray(category.items) ? category.items.length : 0), 0);
};

/**
 * Log a specific issue for markdown bug report format
 * 
 * @param {Object} params
 * @param {string} params.type - 'leftover' | 'grocery' | 'recipe' | 'price'
 * @param {string} params.productName - Product name
 * @param {Object} params.expected - What was expected
 * @param {Object} params.actual - What actually happened
 * @param {string} params.explanation - Explanation in Russian
 */
export const logTestFlowIssue = ({
  type,
  productName,
  expected,
  actual,
  explanation = '',
}) => {
  const issue = {
    type,
    productName,
    expected,
    actual,
    explanation,
    timestamp: new Date().toISOString(),
  };
  
  console.log('[TESTFLOW_ISSUE]', JSON.stringify(issue, null, 2));
  
  // Generate markdown for bug report
  const markdown = `
### Issue: ${productName}

**Type:** ${type}

**Expected:**
\`\`\`json
${JSON.stringify(expected, null, 2)}
\`\`\`

**Actual:**
\`\`\`json
${JSON.stringify(actual, null, 2)}
\`\`\`

**Explanation (RU):**
${explanation}

---
`;
  
  console.log('[TESTFLOW_ISSUE_MD]', markdown);
  
  return { issue, markdown };
};

const testFlowLogger = {
  storePromptForTestFlow,
  getStoredPrompt,
  clearStoredPrompt,
  logTestFlow,
  logTestFlowIssue,
};

export default testFlowLogger;
